/**
 * @fileoverview Payment Business Logic Helpers
 *
 * This module contains core business logic for payment processing, including:
 * - Payment form default value calculation from existing payment data
 * - Payment editability determination based on Lob integration status
 * - Payment type detection (regular vs Lob payments)
 * - Payment submission data preparation for GraphQL mutations
 * - Payment status and job status display formatting
 * - URL generation for payment navigation
 *
 * Business Context:
 * - Payments can be regular staff-recorded payments or automated Lob check payments
 * - Lob payments have special restrictions and job statuses that affect editability
 * - Payments can be associated with multiple reports and facilities
 * - Payment reconciliation is handled separately by finance team
 *
 * @author Extracted during PaymentDialog refactoring
 * @since Phase 1 refactoring - business logic extraction
 */

import { PaymentQuery, PaymentStatus, Payee } from "generated-graphql/graphql";

import { transformStringToNumber, safeParseFloat } from "./numberUtils";
import { PayeeFormInputs, PaymentType, Association } from "./types";

function isDefinedString(value: string | undefined): value is string {
  return Boolean(value);
}

/**
 * Build default associations for new payment creation.
 *
 * Association precedence:
 * 1. report associations (primary report + additional report IDs)
 * 2. submission associations
 * 3. facility associations
 */
export function buildDefaultAssociationsForNewPayment(
  existingPayment: any | null,
  reportId?: string,
  associatedReportIds?: string[],
  associatedSubmissionIds?: string[],
  associatedFacilityIds?: string[]
): Association[] | undefined {
  if (existingPayment) {
    return undefined;
  }

  const dedupedReportIds = Array.from(
    new Set([reportId, ...(associatedReportIds ?? [])].filter(isDefinedString))
  );

  if (dedupedReportIds.length > 0) {
    return dedupedReportIds.map((id) => ({
      amount: 0,
      reportId: id,
    }));
  }

  if (associatedSubmissionIds?.length) {
    return associatedSubmissionIds.filter(isDefinedString).map((submissionId) => ({
      amount: 0,
      submissionId,
    }));
  }

  if (associatedFacilityIds?.length) {
    return associatedFacilityIds.filter(isDefinedString).map((facilityId) => ({
      amount: 0,
      facilityId,
    }));
  }

  return undefined;
}

/**
 * Calculate default form values for payment creation/editing
 *
 * This function determines appropriate default values for the payment form based on:
 * - Existing payment data (for editing)
 * - Associated reports and facilities (for new payments)
 * - Activity context and tenant information
 * - Default payee type (for new payments)
 *
 * Business Rules:
 * - New payments default to billable=true and requeueJob=true
 * - Existing payments preserve their current values
 * - Paid payments cannot be requeued (requeueJob=false)
 * - Associations are built from payment relationships or provided IDs
 * - Default payee type can be specified for new payments
 *
 * @param existingPayment - Payment data when editing, null for new payments
 * @param reportId - Primary report ID to associate with payment
 * @param associatedReportIds - Additional report IDs for multi-report payments
 * @param activityId - Activity context for the payment
 * @param tenantId - Organization ID for the payment
 * @param associations - Associations to associate with the payment (facility and reports)
 * @param defaultPayeeType - Default payee type to pre-select for new payments
 * @returns Complete form default values object
 */
export function calculatePaymentDefaults(
  existingPayment: any | null,
  reportId?: string,
  associatedReportIds?: string[],
  activityId?: string,
  tenantId?: string,
  associations?: Association[],
  defaultPayeeType?: Payee
): PayeeFormInputs {
  const isPaid = existingPayment?.status === PaymentStatus.Paid;

  let calcualtedAssociations = associations;
  if (!calcualtedAssociations) {
    calcualtedAssociations = existingPayment
      ? [
          ...(existingPayment.tierIIReports?.map((report: any) => ({
            amount: safeParseFloat(String(report.amount ?? 0)) ?? 0,
            transactionFeeAmount: report.transactionFeeAmount ?? undefined,
            lateFeeAmount: report.lateFeeAmount ?? undefined,
            reportId: report.tierIIReport?.id,
          })) ?? []),
          ...(existingPayment.facilities?.map((facility: any) => ({
            amount: safeParseFloat(String(facility.amount ?? 0)) ?? 0,
            transactionFeeAmount: facility.transactionFeeAmount ?? undefined,
            lateFeeAmount: facility.lateFeeAmount ?? undefined,
            facilityId: facility.facility?.id,
          })) ?? []),
          ...(existingPayment.submissions?.map((submission: any) => ({
            amount: safeParseFloat(String(submission.amount ?? 0)) ?? 0,
            submissionId: submission.submission?.id,
          })) ?? []),
        ]
      : [
          // Primary report association (if provided)
          ...(reportId ? [{ amount: 0, reportId }] : []),
          // Additional report associations (if provided)
          ...(associatedReportIds ?? [])
            .filter((id) => id && id !== reportId) // Avoid duplicates
            .map((id) => ({ amount: 0, reportId: id })),
        ];
  }

  const values = {
    type: existingPayment ? PaymentType.RECORD_PAYMENT : (undefined as any),
    recipientName: existingPayment?.payee ?? undefined,
    paymentMethod: existingPayment?.paymentMethod ?? undefined,
    payeeType: existingPayment?.payeeType ?? defaultPayeeType,
    companyName: existingPayment?.company ?? undefined,
    addressLine1: existingPayment?.addressLine1 ?? undefined,
    addressLine2: existingPayment?.addressLine2 ?? undefined,
    city: existingPayment?.city ?? undefined,
    state: existingPayment?.state ?? undefined,
    zip: existingPayment?.zip ?? undefined,
    amount: existingPayment?.amount ?? undefined,
    transactionFee: existingPayment?.transactionFee ?? undefined,
    lateFee: existingPayment?.lateFee ?? undefined,
    memo: existingPayment?.memo ?? undefined,
    invoiceNumber: existingPayment?.paymentInvoices?.[0]?.invoiceNumber ?? undefined,
    paidAt: existingPayment?.paidAt ?? undefined,
    associations: calcualtedAssociations,
    paymentInvoices:
      existingPayment?.paymentInvoices?.map((pi: any) => ({
        id: pi.id,
        description: pi.description || "Payment Invoice",
        invoiceNumber: pi.invoiceNumber || "",
        invoiceDocumentId: pi.invoiceDocument?.id || "",
        invoiceDocument: pi.invoiceDocument
          ? {
              id: pi.invoiceDocument.id,
              title: pi.invoiceDocument.title,
              documentType: pi.invoiceDocument.documentType,
              fileExtension: pi.invoiceDocument.fileExtension,
            }
          : undefined,
      })) || [],
    receiptDocument: existingPayment?.document ?? undefined,
    activityId: existingPayment?.activityId ?? activityId,
    status: existingPayment?.status ?? undefined,
    requeueJob: !isPaid,
    billable: existingPayment?.billable ?? true,
    comment: existingPayment?.exceptionNote ?? undefined,
    tenantId: tenantId ?? "",
  };

  return values;
}

/**
 * Determine payment editability based on Lob integration status
 *
 * This function analyzes the payment's Lob integration state to determine
 * what editing operations are allowed. Lob payments have complex state
 * management due to external API integration.
 *
 * Business Rules:
 * - Existing Lob payments (with lobId) cannot edit payment/payee details to prevent duplicates
 * - Active Lob jobs (RUNNING, QUEUED) lock the payment from editing
 * - Failed/cancelled Lob jobs can be requeued by editing the payment
 * - Succeeded Lob jobs allow metadata editing but not payment/payee details
 * - Lob payments with no job record (check sent but no job tracking) allow metadata editing
 * - Regular payments without Lob integration are always editable
 *
 * @param paymentId - Payment ID (undefined for new payments)
 * @param existingPayment - Payment data with potential Lob job information
 * @returns Object containing Lob status flags and granular editability determination
 */
export function determinePaymentEditability(
  paymentId?: string,
  existingPayment?: any
): {
  hasLobJob: boolean;
  hasRequeueableLobJob: boolean;
  hasSucceededLobJob: boolean;
  isExistingLobPayment: boolean;
  notEditable: boolean;
  payeeNotEditable: boolean;
  paymentDetailsNotEditable: boolean;
  metadataEditable: boolean;
} {
  const isExistingLobPayment = !!paymentId && !!existingPayment?.document?.lobId;
  const hasLobJob = !!existingPayment?.job;
  const hasRequeueableLobJob =
    hasLobJob &&
    (existingPayment?.job?.status === "CANCELLED" ||
      existingPayment?.job?.status?.startsWith("FAILED"));
  const hasSucceededLobJob = hasLobJob && existingPayment?.job?.status === "SUCCEEDED";

  // Overall editability (for the save button and general form state)
  // Allow editing if: not a Lob payment, OR has succeeded job, OR has no job (check sent but no job tracking)
  const notEditable =
    isExistingLobPayment && hasLobJob && !hasRequeueableLobJob && !hasSucceededLobJob;

  // Payee section editability (recipient, company, address)
  // Block payee editing for existing Lob payments to prevent duplicate checks
  const payeeNotEditable = isExistingLobPayment || (hasLobJob && !hasRequeueableLobJob);

  // Payment details editability (amount, method, memo, invoice number, paid date)
  // Block payment details editing for existing Lob payments to prevent duplicate checks
  const paymentDetailsNotEditable = isExistingLobPayment || (hasLobJob && !hasRequeueableLobJob);

  // Metadata editability (associations, documents, reconciliation, billable status)
  // Disable metadata editing when ANY Lob job is in progress (running, queued, etc.)
  // This prevents users from modifying payment state while external system is processing
  // Allow metadata editing for:
  // 1. Payments with no job
  // 2. Payments with requeueable jobs (failed/cancelled)
  // 3. Payments with succeeded jobs
  const isLobJobInProgress = hasLobJob && !hasRequeueableLobJob && !hasSucceededLobJob;
  const metadataEditable = !isLobJobInProgress;

  return {
    hasLobJob,
    hasRequeueableLobJob: hasRequeueableLobJob || false,
    hasSucceededLobJob,
    isExistingLobPayment,
    notEditable,
    payeeNotEditable,
    paymentDetailsNotEditable,
    metadataEditable,
  };
}

/**
 * Generate URLs for payment navigation and linking
 *
 * Creates standardized URLs for payment pages that can be used for:
 * - Navigation within the application
 * - Copying links for external sharing
 * - Reconciliation workflow tracking
 *
 * @param paymentId - The payment ID to link to
 * @param reportId - Optional report ID for report-specific payment URLs
 * @returns Complete URL string for the payment page
 */
export function generatePaymentUrl(paymentId: string, reportId?: string): string {
  const currentUrl = window.location.href;
  const url = new URL(currentUrl);

  if (reportId) {
    // For report-specific payments
    return `${url.origin}/staff/fulfillment/r/${reportId}/payments/${paymentId}`;
  } else {
    // For general staff payments
    return `${url.origin}/staff/payments/${paymentId}`;
  }
}

/**
 * Detect if a payment is a Lob payment
 *
 * Determines whether a payment uses Lob check mailing service based on:
 * - Payment type selection (LOB_CHECK)
 * - Existing payment with Lob ID (already sent via Lob)
 * - Existing payment with active Lob job
 *
 * This is used to apply Lob-specific validation and UI restrictions.
 *
 * @param paymentType - Form-selected payment type
 * @param existingPayment - Payment data to check for Lob indicators
 * @param paymentId - Payment ID (undefined for new payments)
 * @returns True if this is a Lob payment, false otherwise
 */
export function isLobPayment(
  paymentType: PaymentType | undefined,
  existingPayment: any,
  paymentId?: string
): boolean {
  const { hasLobJob, isExistingLobPayment } = determinePaymentEditability(
    paymentId,
    existingPayment
  );

  return paymentType === PaymentType.LOB_CHECK || hasLobJob || isExistingLobPayment;
}

/**
 * Prepare payment data for GraphQL submission
 *
 * Transforms form data into the correct format for payment mutations,
 * handling differences between Lob payments and regular payments.
 *
 * Business Logic:
 * - Lob payments require specific fields (recipient, company, address)
 * - Regular payments require different fields (payee, paymentMethod)
 * - Associations are mapped to correct reportId/facilityId structure
 * - Existing payment invoices are merged with new ones
 *
 * @param data - Form data from payment dialog
 * @param paymentId - Payment ID for updates (undefined for new payments)
 * @param existingPayment - Current payment data for merging
 * @param tenantId - Organization ID for the payment
 * @returns Formatted data ready for GraphQL mutation
 */
export function preparePaymentSubmissionData(
  data: PayeeFormInputs,
  paymentId: string | undefined,
  existingPayment: any | null,
  tenantId: string | undefined
) {
  // This function should only be used for regular payments (not Lob payments)
  // Lob payments use prepareLobCheckData and the sendLobCheck mutation instead
  return {
    id: paymentId,
    associations: data.associations.map((r: Association) => ({
      reportId: r.reportId,
      facilityId: r.facilityId,
      submissionId: r.submissionId,
      amount: transformStringToNumber(String(r.amount)),
    })),
    activityId: data.activityId,
    tenantId: tenantId ?? "",
    amount: transformStringToNumber(String(data.amount)),
    // Always send null (not undefined) when no fee is entered so the server overwrites
    // any previously-recorded fee. Sending undefined makes Prisma skip the column.
    transactionFee:
      data.transactionFee == null ? null : transformStringToNumber(String(data.transactionFee)),
    lateFee: data.lateFee == null ? null : transformStringToNumber(String(data.lateFee)),
    payeeType: data.payeeType,
    memo: data.memo,
    company: data.companyName,
    addressLine1: data.addressLine1,
    addressLine2: data.addressLine2,
    city: data.city,
    state: data.state,
    zip: data.zip,
    invoiceNumber: data.invoiceNumber,
    // Required fields for PaymentInput type:
    payee: data.recipientName,
    paymentMethod: data.paymentMethod,
    paidAt: data.paidAt ?? new Date().toISOString(),
    // Optional fields for regular payments:
    documentId: data.receiptDocument?.id,
    paymentInvoices: [
      // Include existing saved invoices
      ...(existingPayment?.paymentInvoices?.map((pi: any) => ({
        id: pi.id,
        invoiceNumber: pi.invoiceNumber ?? "",
        invoiceDocumentId: pi.invoiceDocument?.id ?? "",
      })) ?? []),
      // Include new pending invoices from form
      ...(data.paymentInvoices?.map((pi: any) => ({
        id: pi.id,
        invoiceNumber: pi.invoiceNumber,
        invoiceDocumentId: pi.invoiceDocumentId,
      })) ?? []),
    ],
    billable: data.billable,
    exceptionNote: data.comment,
  };
}

/**
 * Get Material-UI color for payment status display
 *
 * Maps payment statuses to appropriate MUI color variants for
 * consistent status indication across the application.
 *
 * @param status - Payment status enum value
 * @returns MUI color variant (success, warning, error, default)
 */
export function getPaymentStatusColor(
  status: PaymentStatus
): "success" | "warning" | "error" | "default" {
  switch (status) {
    case PaymentStatus.Paid:
      return "success";
    case PaymentStatus.Pending:
    case PaymentStatus.AwaitingInvoice:
      return "warning";
    case PaymentStatus.Cancelled:
    case PaymentStatus.Void:
    case PaymentStatus.Failure:
      return "error";
    default:
      return "default";
  }
}

/**
 * Get Material-UI color for Lob job status display
 *
 * Maps Lob job statuses to appropriate colors for status chips
 * and indicators in the payment interface.
 *
 * @param status - Lob job status string
 * @returns MUI color variant for job status display
 */
export function getJobStatusColor(status: string): "success" | "info" | "error" | "default" {
  if (status === "SUCCEEDED") return "success";
  if (status.startsWith("FAILED")) return "error";
  if (status === "RUNNING") return "info";
  return "default";
}

/**
 * Check for unsaved associations changes
 *
 * This function checks if there are any unsaved changes to the associations
 * in the payment.
 *
 * @param paymentId - Payment ID (undefined for new payments)
 * @param existingPayment - Current payment data for merging
 * @param pendingAssociations - Pending associations from the form
 */
export function checkForUnsavedAssociationsChanges(
  paymentId: string | undefined,
  existingPayment: PaymentQuery["payment"] | null | undefined,
  pendingAssociations: Association[]
): boolean {
  if (!paymentId || !existingPayment) {
    return (pendingAssociations?.length ?? 0) > 0;
  }
  const existingAssociations = [
    ...(existingPayment?.tierIIReports?.map((report: any) => ({
      amount: safeParseFloat(String(report.amount ?? 0)) ?? 0,
      reportId: report.tierIIReport?.id,
      facilityId: undefined,
      submissionId: undefined,
    })) ?? []),
    ...(existingPayment?.facilities?.map((facility: any) => ({
      amount: safeParseFloat(String(facility.amount ?? 0)) ?? 0,
      facilityId: facility.facility?.id,
      reportId: undefined,
      submissionId: undefined,
    })) ?? []),
    ...(existingPayment?.submissions?.map((submission: any) => ({
      amount: safeParseFloat(String(submission.amount ?? 0)) ?? 0,
      submissionId: submission.submission?.id,
      reportId: undefined,
      facilityId: undefined,
    })) ?? []),
  ];
  return existingAssociations.some((association) => {
    const pendingAssociation = pendingAssociations?.find(
      (p) =>
        p.reportId === association.reportId ||
        p.facilityId === association.facilityId ||
        p.submissionId === association.submissionId
    );
    return pendingAssociation?.amount !== association.amount;
  });
}
